/**
 * Shared helpers for the operations manual + setup manual + help pages.
 *   - renderBody: tiny markdown → HTML (bold / inline code / newlines)
 *   - FaqRow: collapsible FAQ row used by the public Help page
 *   - SetupVideo: per-section onboarding video, sourced from clinic settings
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Languages, Volume2, CheckCircle2 } from 'lucide-react';

// Module-level watched-videos cache shared by every player on the page.
let _watchedPromise = null;
function fetchWatchedSet() {
  if (!_watchedPromise) {
    _watchedPromise = import('../../lib/api')
      .then(m => m.api.get('/training/me'))
      .then(r => new Set(r.data?.watched || []))
      .catch(() => new Set());
  }
  return _watchedPromise;
}

/**
 * Very minimal markdown → HTML for the manual body. We only need:
 *   • bold (**...** → <strong>)
 *   • newlines (→ <br>)
 *   • backtick-code (`...` → <code>)
 * That keeps the content authorable in plain text without pulling in a
 * heavy markdown library.
 */
export function renderBody(text) {
  const esc = (s) => s.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  return esc(text)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-fg/10 text-[12px]">$1</code>')
    .replace(/\n/g, '<br/>');
}

export function FaqRow({ f, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <details data-testid={testid} className="surface border border-soft rounded-lg overflow-hidden" open={open} onToggle={e=>setOpen(e.target.open)}>
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center justify-between hover:bg-fg/5">
        {f.q}
        <ChevronRight className={`w-4 h-4 text-fg-muted shrink-0 ml-2 transition-transform ${open ? 'rotate-90' : ''}`}/>
      </summary>
      <div className="px-4 pb-3 text-sm text-fg-secondary leading-relaxed">{f.a}</div>
    </details>
  );
}

/**
 * SetupVideo — reads `clinic_settings.setup_videos[stepId]` and renders the
 * appropriate player.  Supports:
 *   - YouTube URLs   (https://www.youtube.com/watch?v=... | https://youtu.be/...)
 *   - Vimeo URLs     (https://vimeo.com/123)
 *   - Direct MP4     (https://...mp4)
 *   - Multi-language base path (e.g. /media/manual-videos/seg01) — renders the
 *     MultiLangVideo player with audio (EN/PL/ES) + animated CC (7 languages).
 *
 * Renders nothing when no URL is configured.
 */
export function SetupVideo({ videoKey }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    import('../../lib/api').then(m => {
      m.api.get('/clinic/settings').then(r => {
        setUrl((r.data?.setup_videos || {})[videoKey] || '');
      }).catch(()=>{});
    });
  }, [videoKey]);

  if (!url) return null;

  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?#]+)/i);
  const vm = url.match(/vimeo\.com\/(\d+)/i);
  const hasExt = /\.[a-z0-9]{2,5}(\?|#|$)/i.test(url);

  return (
    <div className="mt-4 rounded-lg overflow-hidden border border-soft" data-testid={`setup-video-${videoKey}`}>
      {yt ? (
        <iframe
          src={`https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`}
          className="w-full aspect-video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Setup walkthrough"
        />
      ) : vm ? (
        <iframe
          src={`https://player.vimeo.com/video/${vm[1]}`}
          className="w-full aspect-video"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Setup walkthrough"
        />
      ) : !hasExt ? (
        <MultiLangVideo base={url} videoKey={videoKey}/>
      ) : (
        // Direct MP4 (or any HTML5 video) fallback.
        <video src={url} controls className="w-full aspect-video bg-black" preload="metadata"/>
      )}
    </div>
  );
}

/* ------------- Multi-language training video player (iter 146) ------------- */

const AUDIO_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
];
const CC_LANGS = [
  { code: 'off', label: 'Captions off' },
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'uk', label: 'Українська' },
  { code: 'it', label: 'Italiano' },
];

// One-time keyframes for the animated captions.
if (typeof document !== 'undefined' && !document.getElementById('pg-cc-style')) {
  const st = document.createElement('style');
  st.id = 'pg-cc-style';
  st.textContent = `
    .pg-cc-box { animation: pgCcIn .35s ease-out both; }
    @keyframes pgCcIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    .pg-cc-word { display: inline-block; opacity: 0; animation: pgCcWord .3s ease-out forwards; }
    @keyframes pgCcWord { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
  `;
  document.head.appendChild(st);
}

function parseVtt(raw) {
  const re = /(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})\.(\d{3})/;
  const cues = [];
  for (const block of raw.split(/\n\s*\n/)) {
    const lines = block.trim().split('\n');
    const ti = lines.findIndex(l => re.test(l));
    if (ti === -1) continue;
    const m = lines[ti].match(re);
    const start = +m[1] * 3600 + +m[2] * 60 + +m[3] + +m[4] / 1000;
    const end = +m[5] * 3600 + +m[6] * 60 + +m[7] + +m[8] / 1000;
    const text = lines.slice(ti + 1).join(' ').trim();
    if (text) cues.push({ start, end, text });
  }
  return cues;
}

export function MultiLangVideo({ base, videoKey }) {
  const [audio, setAudioState] = useState(() => localStorage.getItem('pg-video-audio') || 'en');
  const [cc, setCcState] = useState(() => localStorage.getItem('pg-video-cc') || 'en');
  const [cues, setCues] = useState([]);
  const [cueIdx, setCueIdx] = useState(-1);
  const [watched, setWatched] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchWatchedSet().then(set => { if (alive) setWatched(set.has(videoKey)); });
    return () => { alive = false; };
  }, [videoKey]);

  const toggleWatched = async (next) => {
    setWatched(next);
    const set = await fetchWatchedSet();
    next ? set.add(videoKey) : set.delete(videoKey);
    try {
      const { api } = await import('../../lib/api');
      if (next) await api.post('/training/watched', { video_key: videoKey });
      else await api.delete(`/training/watched/${videoKey}`);
    } catch { /* offline — local state already reflects intent */ }
  };

  const setAudio = v => { setAudioState(v); localStorage.setItem('pg-video-audio', v); setCueIdx(-1); };
  const setCc = v => { setCcState(v); localStorage.setItem('pg-video-cc', v); setCueIdx(-1); };

  useEffect(() => {
    if (cc === 'off') { setCues([]); return; }
    let alive = true;
    fetch(`${base}-${audio}.${cc}.vtt`)
      .then(r => (r.ok ? r.text() : ''))
      .then(t => { if (alive) setCues(t.startsWith('WEBVTT') ? parseVtt(t) : []); })
      .catch(() => { if (alive) setCues([]); });
    return () => { alive = false; };
  }, [base, audio, cc]);

  const onTime = () => {
    const t = videoRef.current?.currentTime ?? 0;
    setCueIdx(cues.findIndex(c => t >= c.start && t <= c.end));
  };

  const cue = cueIdx >= 0 ? cues[cueIdx] : null;

  return (
    <div>
      <div className="relative">
        <video
          ref={videoRef} key={audio} src={`${base}-${audio}.mp4`} controls preload="metadata"
          className="w-full aspect-video bg-black" onTimeUpdate={onTime}
          onEnded={() => !watched && toggleWatched(true)}
          data-testid={`setup-video-player-${videoKey}`}
        />
        {cue && (
          <div key={`${cc}-${cueIdx}`} className="pg-cc-box absolute inset-x-0 bottom-16 flex justify-center px-6 pointer-events-none" data-testid={`video-cc-overlay-${videoKey}`}>
            <div className="max-w-[85%] rounded-lg bg-black/70 backdrop-blur-sm px-4 py-2 text-center text-white text-sm md:text-base leading-snug shadow-lg">
              {cue.text.split(' ').map((w, i) => (
                <span key={i} className="pg-cc-word" style={{ animationDelay: `${Math.min(i * 45, 900)}ms` }}>{w}&nbsp;</span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 flex-wrap px-3 py-2 bg-fg/5 border-t border-soft">
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          <Volume2 className="w-3.5 h-3.5"/> Audio
          <select
            className="text-xs bg-transparent border border-soft rounded-md px-1.5 py-1 text-fg cursor-pointer"
            value={audio} onChange={e => setAudio(e.target.value)}
            data-testid={`video-audio-lang-${videoKey}`}
          >
            {AUDIO_LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          <Languages className="w-3.5 h-3.5"/> Subtitles
          <select
            className="text-xs bg-transparent border border-soft rounded-md px-1.5 py-1 text-fg cursor-pointer"
            value={cc} onChange={e => setCc(e.target.value)}
            data-testid={`video-cc-lang-${videoKey}`}
          >
            {CC_LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => toggleWatched(!watched)}
          className={`ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition ${watched ? 'border-[#2E7D4F]/40 bg-[#2E7D4F]/10 text-[#2E7D4F]' : 'border-soft text-fg-muted hover:border-brand/40 hover:text-fg'}`}
          data-testid={`video-watched-toggle-${videoKey}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5"/>
          {watched ? 'Watched' : 'Mark watched'}
        </button>
      </div>
    </div>
  );
}
